import { useState, useEffect, useRef } from 'react';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search as SearchIcon, X, Loader2, ExternalLink, FileSearch, FileText, CalendarIcon, Bell, Download, Mail, AlertTriangle, LogIn, TrendingUp, HelpCircle, Brain } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import SearchResultsAI from '@/components/SearchResultsAI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { formatDateLocalized } from '@/hooks/useLocationDetection';
import CpvSearch from '@/components/CpvSearch';
import { useTrackActivity } from '@/hooks/useActivityTracker';
import CpvBadge from '@/components/CpvBadge';
import { cn } from '@/lib/utils';

type DatePreset = 'week' | 'month' | '3months' | '6months' | 'custom' | '';

const DATE_PRESET_KEYS: DatePreset[] = ['week', 'month', '3months', '6months', 'custom'];

interface WinnerInfo {
  lotNumber?: string;
  name: string;
  vatNumber: string;
}

interface TedResult {
  id: string;
  ted_notice_id: string;
  title: string;
  description: string;
  buyer_name: string;
  country: string;
  cpv_codes: string[];
  estimated_value: number | null;
  currency: string;
  publication_date: string;
  deadline_date: string | null;
  ted_url: string;
  pdf_url: string;
  notice_type?: string;
  winner_name?: string;
  winner_vat_number?: string;
  winners?: WinnerInfo[];
}

// Notice type keys for i18n lookup
const NOTICE_TYPE_KEYS = [
  'cn-standard', 'can-standard', 'pin-only', 'veat', 'can-modif',
  'qu-sy', 'subco', 'pin-buyer', 'pin-cfc-social', 'pin-rtl',
];
import { EU_COUNTRIES, getCountryName } from '@/lib/countries';
import { useTranslation } from 'react-i18next';

// detect-location returns ISO alpha-2 (e.g. "IT"), while the UI list uses alpha-3 (e.g. "ITA")
const ALPHA2_TO_ALPHA3: Record<string, string> = {
  IT: 'ITA', DE: 'DEU', FR: 'FRA', ES: 'ESP', NL: 'NLD',
  BE: 'BEL', AT: 'AUT', PL: 'POL', PT: 'PRT', GR: 'GRC',
  SE: 'SWE', DK: 'DNK', FI: 'FIN', IE: 'IRL', CZ: 'CZE',
  RO: 'ROU', HU: 'HUN', BG: 'BGR', HR: 'HRV', SK: 'SVK',
  SI: 'SVN', LT: 'LTU', LV: 'LVA', EE: 'EST', LU: 'LUX',
  MT: 'MLT', CY: 'CYP',
};

function normalizeCountryCode(code: string): string {
  const upper = (code || '').toUpperCase().trim();
  if (!upper) return '';
  if (ALPHA2_TO_ALPHA3[upper]) return ALPHA2_TO_ALPHA3[upper];
  // If already a valid alpha-3 EU code, keep it; otherwise discard non-EU codes
  const validAlpha3 = new Set(Object.values(ALPHA2_TO_ALPHA3));
  if (validAlpha3.has(upper)) return upper;
  return '';
}

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clientId } = useAuth();
  const { remainingSearches, dailySearchLimit, todaySearchCount, exportEnabled, isAdmin, isSubscribed, refreshSubscription } = useSubscription();
  
  // Free tier limitations
  const isFreeUser = !isSubscribed && !isAdmin;
  const FREE_RESULTS_LIMIT = 3;
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'it';
  const trackActivity = useTrackActivity();
  
  const [keywords, setKeywords] = useState('');
  const [cpvCodes, setCpvCodes] = useState<string[]>([]);
  const [country, setCountry] = useState<string | null>(null);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [datePreset, setDatePreset] = useState<DatePreset>('');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [onlyWithDeadline, setOnlyWithDeadline] = useState(false);
  const [creatingMonitoring, setCreatingMonitoring] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [noticeType, setNoticeType] = useState<string>('');

  // Pre-select country based on detected location (only on first mount, null means not yet set)
  useEffect(() => {
    // Only apply auto-detection if country has never been set (null)
    // Empty string means user explicitly chose "all countries"
    if (country !== null) return;

    const tryApplyDetected = () => {
      const detected = localStorage.getItem('detectedCountry');
      if (detected) {
        setCountry(normalizeCountryCode(detected));
        return true;
      }
      return false;
    };

    if (tryApplyDetected()) return;

    let tries = 0;
    const interval = window.setInterval(() => {
      tries += 1;
      if (tryApplyDetected() || tries >= 20) {
        window.clearInterval(interval);
      }
    }, 250);

    return () => window.clearInterval(interval);
  }, [country]);

  // Pre-fill CPV codes from URL query params (e.g. ?cpv=12345678-9)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cpvParam = params.get('cpv');
    if (cpvParam) {
      setCpvCodes(cpvParam.split(',').filter(Boolean));
    }
  }, [location.search]);

  const handleDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();
    
    switch (preset) {
      case 'week':
        setDateFrom(subDays(today, 7));
        setDateTo(today);
        break;
      case 'month':
        setDateFrom(subMonths(today, 1));
        setDateTo(today);
        break;
      case '3months':
        setDateFrom(subMonths(today, 3));
        setDateTo(today);
        break;
      case '6months':
        setDateFrom(subMonths(today, 6));
        setDateTo(today);
        break;
      case 'custom':
        // Keep current dates or clear them
        break;
      default:
        setDateFrom(undefined);
        setDateTo(undefined);
    }
  };

  const clearDates = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setDatePreset('');
  };
  const [results, setResults] = useState<TedResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [lastSearchBody, setLastSearchBody] = useState<any>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  // Page cache: store fetched pages to avoid re-calling the API
  const pageCacheRef = useRef<Map<number, TedResult[]>>(new Map());
  const { toast } = useToast();

  // Send to Analysis state
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [analysisDatasets, setAnalysisDatasets] = useState<{ id: string; name: string }[]>([]);
  const [selectedAnalysisDatasetId, setSelectedAnalysisDatasetId] = useState('');
  const [newAnalysisDatasetName, setNewAnalysisDatasetName] = useState('');
  const [sendingAllToAnalysis, setSendingAllToAnalysis] = useState(false);

  const RESULTS_PER_PAGE = 50;
  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);

  const handleSearch = async (page: number = 1, useCache: boolean = false) => {
    const keywordList = keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    const hasSearchCriteria = keywordList.length > 0 || cpvCodes.length > 0 || !!country;

    if (!hasSearchCriteria) {
      toast({
        variant: "destructive",
        title: t('search.searchCriteria'),
        description: t('search.searchCriteriaDesc'),
      });
      return;
    }

    // If it's a new search (page 1 without cache flag), clear the cache
    if (page === 1 && !useCache) {
      pageCacheRef.current.clear();
    }

    // Check if page is already cached
    if (useCache && pageCacheRef.current.has(page)) {
      setResults(pageCacheRef.current.get(page)!);
      setCurrentPage(page);
      return;
    }

    setSearching(true);
    if (!useCache) setResults([]);
    if (page === 1 && !useCache) {
      trackActivity('search', { keywords: keywordList, cpv_codes: cpvCodes, country });
    }

    try {
      const body = {
        ...(keywordList.length > 0 ? { keywords: keywordList } : {}),
        ...(cpvCodes.length > 0 ? { cpv_codes: cpvCodes } : {}),
        ...(country ? { country } : {}),
        ...(minValue ? { min_value: parseFloat(minValue) } : {}),
        ...(maxValue ? { max_value: parseFloat(maxValue) } : {}),
        ...(dateFrom ? { date_from: format(dateFrom, 'yyyyMMdd') } : {}),
        ...(dateTo ? { date_to: format(dateTo, 'yyyyMMdd') } : {}),
        ...(noticeType ? { notice_type: noticeType } : {}),
        include_expired: includeExpired,
        only_with_deadline: onlyWithDeadline,
        page: page,
        limit: RESULTS_PER_PAGE,
      };

      setLastSearchBody(body);

      const { data, error } = await supabase.functions.invoke('ted-search', {
        body,
      });

      if (error) throw error;
      
      // Check if limit was reached (edge function returns limit_reached flag)
      if (data?.limit_reached) {
        refreshSubscription();
        setLimitReached(true);
        return;
      }
      
      if (data?.error) throw new Error(data.error);

      const rawResults = data?.results || [];
      let newResults = [...rawResults];
      
      // Sort by deadline date (closest first, nulls last)
      newResults.sort((a: TedResult, b: TedResult) => {
        if (!a.deadline_date && !b.deadline_date) return 0;
        if (!a.deadline_date) return 1;
        if (!b.deadline_date) return -1;
        return new Date(a.deadline_date).getTime() - new Date(b.deadline_date).getTime();
      });
      
      // Cache the page results
      pageCacheRef.current.set(page, newResults);
      
      setResults(newResults);
      setTotalResults(data?.total || newResults.length);
      setCurrentPage(page);
      
      if (newResults.length === 0 && page === 1) {
        toast({
          title: t('search.noResultsToast'),
          description: t('search.noResultsToastDesc'),
        });
      } else if (page === 1) {
        toast({
          title: t('search.searchCompleteToast'),
          description: t('search.foundTendersToast', { count: data.total || newResults.length }),
        });
      }
      
      // Refresh subscription to update search counter only on first page
      if (page === 1) refreshSubscription();
    } catch (error: any) {
      console.error('Search error:', error);
      
      // Only treat 401 status as real authentication error
      const isAuthError = error.status === 401;
      
      if (isAuthError) {
        setAuthError(t('search.sessionExpiredDesc'));
      } else {
        toast({
          variant: "destructive",
          title: t('search.searchError'),
          description: error.message || t('search.searchErrorDesc'),
        });
      }
    } finally {
      setSearching(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      handleSearch(newPage, true);
    }
  };

  // Create monitoring from current search
  const handleCreateMonitoring = async () => {
    if (!user || !clientId) {
      toast({
        variant: "destructive",
        title: t('search.accessRequired'),
        description: t('search.accessRequiredDesc'),
      });
      return;
    }

    const keywordList = keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    if (keywordList.length === 0 && cpvCodes.length === 0 && !country) {
      toast({
        variant: "destructive",
        title: t('search.noCriteria'),
        description: t('search.noCriteriaDesc'),
      });
      return;
    }

    setCreatingMonitoring(true);
    try {
      const monitoringName = keywordList.length > 0 
        ? keywordList.slice(0, 2).join(', ') 
        : cpvCodes.length > 0 
          ? `CPV: ${cpvCodes[0]}` 
          : `Paese: ${country}`;

      const { error } = await supabase.from('ted_searches').insert({
        client_id: clientId,
        name: monitoringName,
        keywords: keywordList,
        cpv_codes: cpvCodes,
        countries: country ? [country] : [],
        min_value: minValue ? parseFloat(minValue) : null,
        max_value: maxValue ? parseFloat(maxValue) : null,
        is_active: true,
      });

      if (error) throw error;

      toast({
        title: t('search.monitoringCreatedToast'),
        description: t('search.monitoringCreatedDesc'),
      });

      navigate('/monitoring');
    } catch (error: any) {
      console.error('Error creating monitoring:', error);
      toast({
        variant: "destructive",
        title: t('search.errorGeneric'),
        description: error.message || t('search.errorCreatingMonitoring'),
      });
    } finally {
      setCreatingMonitoring(false);
    }
  };

  // Send test email
  const handleSendTestEmail = async () => {
    if (!user?.email) {
      toast({
        variant: "destructive",
        title: t('search.emailNotAvailable'),
        description: t('search.emailNotAvailableDesc'),
      });
      return;
    }

    if (results.length === 0) {
      toast({
        variant: "destructive",
        title: t('search.noResultsEmail'),
        description: t('search.noResultsEmailDesc'),
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      // Escape HTML for security
      const escapeHtml = (str: string) => {
        return str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };

      const sampleResults = results.slice(0, 5);
      const resultsHtml = sampleResults.map(r => `
        <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
          <h3 style="margin: 0 0 8px 0; color: #1f2937;">${escapeHtml(r.title)}</h3>
          <p style="color: #6b7280; font-size: 14px; margin: 0 0 8px 0;">${escapeHtml(r.buyer_name || 'N/D')}</p>
          ${r.estimated_value ? `<p style="color: #059669; font-weight: bold;">€${r.estimated_value.toLocaleString()}</p>` : ''}
          ${r.deadline_date ? `<p style="color: #dc2626; font-size: 14px;">Scadenza: ${formatDateLocalized(r.deadline_date)}</p>` : ''}
          ${r.pdf_url ? `<a href="${r.pdf_url}" style="color: #2563eb;">Visualizza PDF</a>` : ''}
        </div>
      `).join('');

      const html = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 16px;">
            🔔 Alert TED Monitor - Test
          </h1>
          <p style="color: #4b5563;">Questa è un'email di test con ${sampleResults.length} risultati di esempio:</p>
          ${resultsHtml}
          <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px;">
            <p>Questa è un'email di test inviata da TED Monitor.</p>
          </div>
        </div>
      `;

      const { data, error } = await supabase.functions.invoke('send-alert-email', {
        body: {
          to: user.email,
          subject: '🔔 TED Monitor - Email di Test',
          html,
        },
      });

      if (error) throw error;

      const messageId = data?.messageId as string | undefined;

      let deliveryInfo = t('search.checkSpam');
      if (messageId) {
        try {
          const { data: statusData } = await supabase.functions.invoke('brevo-email-status', {
            body: { email: user.email, messageId },
          });

          const latestEvent = statusData?.latest?.event as string | undefined;
          const latestReason = statusData?.latest?.reason as string | undefined;
          if (latestEvent) {
            deliveryInfo = latestReason
              ? t('search.brevoStatusWithReason', { event: latestEvent, reason: latestReason })
              : t('search.brevoStatus', { event: latestEvent });
          }
        } catch (e) {
          // ignore status errors
        }
      }

      toast({
        title: t('search.emailSent'),
        description: messageId
          ? t('search.emailSentTo', { email: user.email, messageId, deliveryInfo })
          : t('search.emailSentToSimple', { email: user.email, deliveryInfo }),
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        variant: "destructive",
        title: t('search.emailError'),
        description: error.message || t('search.emailErrorDesc'),
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Get export data
  // Export data with multi-winner support
  const getExportData = () => {
    // Determine the maximum number of winners across all results
    const maxWinners = results.reduce((max, r) => {
      const winnerCount = r.winners?.length || (r.winner_name ? 1 : 0);
      return Math.max(max, winnerCount);
    }, 0);

    return results.map(r => {
      const baseData: Record<string, string | number> = {
        [t('search.exportHeaders.tenderId')]: r.ted_notice_id,
        [t('search.exportHeaders.title')]: r.title,
        [t('search.exportHeaders.description')]: r.description,
        [t('search.exportHeaders.buyer')]: r.buyer_name,
        [t('search.exportHeaders.country')]: r.country,
        [t('search.exportHeaders.estimatedValue')]: r.estimated_value || '',
        [t('search.exportHeaders.currency')]: r.currency,
        [t('search.exportHeaders.publicationDate')]: r.publication_date,
        [t('search.exportHeaders.deadline')]: r.deadline_date || '',
        [t('search.exportHeaders.cpvCodes')]: r.cpv_codes?.join(', ') || '',
      };

      // Add winner columns dynamically
      if (maxWinners > 0) {
        const winners = r.winners && r.winners.length > 0 
          ? r.winners 
          : (r.winner_name ? [{ name: r.winner_name, vatNumber: r.winner_vat_number || '', lotNumber: '' }] : []);

        for (let i = 0; i < maxWinners; i++) {
          const winner = winners[i];
          const suffix = maxWinners > 1 ? ` ${i + 1}` : '';
          baseData[`${t('search.exportHeaders.lot')}${suffix}`] = winner?.lotNumber || '';
          baseData[`${t('search.exportHeaders.winner')}${suffix}`] = winner?.name || '';
          baseData[`${t('search.exportHeaders.winnerVat')}${suffix}`] = winner?.vatNumber || '';
        }
      }

      baseData[t('search.exportHeaders.pdfUrl')] = r.pdf_url || '';
      return baseData;
    });
  };

  // Export results to Excel
  const handleExportExcel = async () => {
    if (results.length === 0) {
      toast({
        variant: "destructive",
        title: t('search.noResultsExport'),
        description: t('search.noResultsExportDesc'),
      });
      return;
    }

    try {
      const { exportRowsToExcel } = await import('@/lib/excel');
      const exportData = getExportData();
      const fileName = `ted_search_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      await exportRowsToExcel(exportData, 'TED Results', fileName);

      toast({
        title: t('search.exportCompleted'),
        description: t('search.exportedResults', { count: results.length }),
      });
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: t('search.exportError'),
        description: t('search.exportErrorDesc'),
      });
    }
  };

  // Export results to CSV
  const handleExportCSV = async () => {
    if (results.length === 0) {
      toast({
        variant: "destructive",
        title: t('search.noResultsExport'),
        description: t('search.noResultsExportDesc'),
      });
      return;
    }

    try {
      const { rowsToCsv } = await import('@/lib/excel');
      const exportData = getExportData();
      const csv = rowsToCsv(exportData, ';');
      
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ted_search_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: t('search.exportCompleted'),
        description: t('search.exportedResults', { count: results.length }),
      });
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: t('search.exportError'),
        description: t('search.exportErrorDesc'),
      });
    }
  };

  // Send ALL results (all pages) to Data Analysis
  const handleSendAllToAnalysis = async () => {
    if (totalResults === 0 || !lastSearchBody) return;
    const { data } = await supabase.from('analysis_datasets').select('id, name').order('created_at', { ascending: false });
    setAnalysisDatasets(data || []);
    if (data && data.length > 0) setSelectedAnalysisDatasetId(data[0].id);
    setShowAnalysisDialog(true);
  };

  const confirmSendAllToAnalysis = async () => {
    setSendingAllToAnalysis(true);
    try {
      let datasetId = selectedAnalysisDatasetId;
      if (!datasetId && newAnalysisDatasetName.trim()) {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) throw new Error('Not authenticated');
        const { data: newDs, error: dsErr } = await supabase
          .from('analysis_datasets')
          .insert({ name: newAnalysisDatasetName.trim(), user_id: currentUser.id })
          .select()
          .single();
        if (dsErr) throw dsErr;
        datasetId = newDs.id;
      }
      if (!datasetId) {
        toast({ variant: 'destructive', title: t('dataAnalysis.selectOrCreate', 'Select or create a dataset') });
        setSendingAllToAnalysis(false);
        return;
      }

      // Fetch ALL pages
      const allResults: TedResult[] = [];
      const pagesToFetch = Math.ceil(totalResults / RESULTS_PER_PAGE);
      
      for (let p = 1; p <= pagesToFetch; p++) {
        if (pageCacheRef.current.has(p)) {
          allResults.push(...pageCacheRef.current.get(p)!);
          continue;
        }
        const body = { ...lastSearchBody, page: p, limit: RESULTS_PER_PAGE };
        const { data, error } = await supabase.functions.invoke('ted-search', { body });
        if (error) throw error;
        if (data?.results) {
          allResults.push(...data.results);
          pageCacheRef.current.set(p, data.results);
        }
      }

      if (allResults.length === 0) {
        setSendingAllToAnalysis(false);
        return;
      }

      // Save results to ted_results (upsert)
      const resultsToInsert = allResults.map(r => ({
        ted_notice_id: r.ted_notice_id,
        title: r.title,
        description: r.description || null,
        buyer_name: r.buyer_name || null,
        country: r.country || null,
        cpv_codes: r.cpv_codes || [],
        estimated_value: r.estimated_value || null,
        currency: r.currency || 'EUR',
        publication_date: r.publication_date || null,
        deadline_date: r.deadline_date || null,
        ted_url: r.ted_url || null,
      }));

      const batchSize = 50;
      const insertedIds: string[] = [];
      for (let i = 0; i < resultsToInsert.length; i += batchSize) {
        const batch = resultsToInsert.slice(i, i + batchSize);
        const { data: inserted, error: insertError } = await supabase
          .from('ted_results')
          .upsert(batch, { onConflict: 'ted_notice_id' })
          .select('id');
        if (insertError) throw insertError;
        if (inserted) insertedIds.push(...inserted.map(r => r.id));
      }

      // Add to dataset
      const items = insertedIds.map(id => ({ dataset_id: datasetId, ted_result_id: id }));
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        await supabase.from('analysis_dataset_items').upsert(batch, { onConflict: 'dataset_id,ted_result_id', ignoreDuplicates: true });
      }

      toast({
        title: t('dataAnalysis.sentToAnalysis', 'Sent to Analysis'),
        description: `${insertedIds.length} ${t('dataAnalysis.tenders', 'tenders')}`,
      });
      setShowAnalysisDialog(false);
      setNewAnalysisDatasetName('');
    } catch (error: any) {
      console.error('Error sending to analysis:', error);
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setSendingAllToAnalysis(false);
    }
  };

  return (
    <DashboardLayout>
      <TooltipProvider delayDuration={0} skipDelayDuration={500}>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('search.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('search.subtitle')}
            </p>
          </div>
          
          {/* Search Counter Badge */}
          {user && !isAdmin && dailySearchLimit !== null && (
            <div className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg border",
              remainingSearches === 0 
                ? "bg-destructive/10 border-destructive/50 text-destructive" 
                : remainingSearches !== null && remainingSearches <= 5
                  ? "bg-amber-500/10 border-amber-500/50 text-amber-600 dark:text-amber-400"
                  : "bg-primary/10 border-primary/50 text-primary"
            )}>
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">
                {remainingSearches === null 
                  ? t('search.unlimitedSearches')
                  : t('search.searchesToday', { remaining: remainingSearches, limit: dailySearchLimit })
                }
              </span>
            </div>
          )}
          
          {/* Admin Unlimited Badge */}
          {user && isAdmin && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-primary/10 border-primary/50 text-primary">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">{t('search.unlimitedAdmin')}</span>
            </div>
          )}
        </div>

        {/* Search Limit Reached Modal - Not shown for admins */}
        <AlertDialog open={user && !isAdmin && limitReached ? true : false}>
          <AlertDialogContent className="sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {t('search.limitReachedTitle', 'Limite ricerche gratuito raggiunto!')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                {t('search.limitReachedDescription', 'Hai esaurito le ricerche giornaliere gratuite. Per continuare a cercare bandi, sottoscrivi un abbonamento.')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction 
                onClick={() => navigate('/pricing')}
                className="w-full gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                {t('search.viewPlans', 'Vedi Piani e Prezzi')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Auth Error Banner */}
        {authError && (
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('search.sessionExpired')}</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{authError}</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-4 gap-2"
                onClick={() => {
                  supabase.auth.signOut();
                  navigate('/auth');
                }}
              >
                <LogIn className="w-4 h-4" />
                {t('search.loginButton')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Search Form - Compact Horizontal Layout */}
        <Card className="border-0 shadow-sm">
          <CardContent className="py-4 space-y-3">
            {/* Row 1: Main filters */}
            <div className="flex flex-wrap items-start gap-3">
              {/* Keywords */}
              <div className="flex-1 min-w-[200px] space-y-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label htmlFor="keywords" className="text-xs cursor-help inline-flex items-center gap-1">
                      {t('search.keywords')}
                      <HelpCircle className="w-3 h-3 text-muted-foreground" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('search.keywordsTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
                <Input
                  id="keywords"
                  placeholder={t('search.keywordsPlaceholder')}
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
              </div>

              {/* CPV - Free users limited to 1 */}
              <div className="w-[250px] space-y-1 relative">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs cursor-help inline-flex items-center gap-1">
                      {t('search.cpvCodes')}
                      {isFreeUser && cpvCodes.length > 0 && (
                        <span className="text-muted-foreground ml-1">(1/{t('search.freeMaxCpv', 'max 1')})</span>
                      )}
                      <HelpCircle className="w-3 h-3 text-muted-foreground" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('search.cpvTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
                <CpvSearch 
                  selectedCodes={cpvCodes}
                  onCodesChange={(codes) => {
                    if (isFreeUser && codes.length > 1) {
                      toast({
                        variant: "destructive",
                        title: t('search.freeLimitTitle', 'Funzionalità limitata'),
                        description: t('search.freeMultiCpvBlocked', 'Gli utenti gratuiti possono selezionare solo 1 codice CPV. Abbonati per ricerche multi-CPV.'),
                      });
                      return;
                    }
                    setCpvCodes(codes);
                  }}
                />
              </div>

              {/* Country */}
              <div className="w-[150px] space-y-1">
                <Label className="text-xs">{t('search.country')}</Label>
                <Select value={country ?? "all"} onValueChange={(val) => setCountry(val === "all" ? "" : val)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('common.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('search.allCountries')}</SelectItem>
                    {EU_COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {getCountryName(c.code, currentLang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notice Type */}
              <div className="w-[180px] space-y-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs cursor-help inline-flex items-center gap-1">
                      {t('search.noticeType', 'Tipo di gara')}
                      <HelpCircle className="w-3 h-3 text-muted-foreground" />
                    </Label>
                  </TooltipTrigger>
                   <TooltipContent side="top" className="max-w-sm">
                     <div className="space-y-1.5 text-xs">
                       <p><strong>{t('search.noticeTypes.cn-standard')}:</strong> {t('search.noticeTypeTooltip.standard')}</p>
                       <p><strong>{t('search.noticeTypes.can-standard')}:</strong> {t('search.noticeTypeTooltip.award')}</p>
                       <p><strong>{t('search.noticeTypes.pin-only')}:</strong> {t('search.noticeTypeTooltip.prior')}</p>
                       <p><strong>{t('search.noticeTypes.veat')}:</strong> {t('search.noticeTypeTooltip.voluntary')}</p>
                       <p><strong>{t('search.noticeTypes.can-modif')}:</strong> {t('search.noticeTypeTooltip.modification')}</p>
                       <p><strong>{t('search.noticeTypes.qu-sy')}:</strong> {t('search.noticeTypeTooltip.qualification')}</p>
                       <p><strong>{t('search.noticeTypes.subco')}:</strong> {t('search.noticeTypeTooltip.subcontracting')}</p>
                     </div>
                   </TooltipContent>
                </Tooltip>
                <Select value={noticeType || "all"} onValueChange={(val) => setNoticeType(val === "all" ? "" : val)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t('common.all', 'Tutti')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all', 'Tutti')}</SelectItem>
                     <SelectItem value="cn-standard">{t('search.noticeTypes.cn-standard')}</SelectItem>
                     <SelectItem value="can-standard">{t('search.noticeTypes.can-standard')}</SelectItem>
                     <SelectItem value="pin-only">{t('search.noticeTypes.pin-only')}</SelectItem>
                     <SelectItem value="veat">{t('search.noticeTypes.veat')}</SelectItem>
                     <SelectItem value="can-modif">{t('search.noticeTypes.can-modif')}</SelectItem>
                     <SelectItem value="qu-sy">{t('search.noticeTypes.qu-sy')}</SelectItem>
                     <SelectItem value="subco">{t('search.noticeTypes.subco')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[100px] space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t('search.minValue', 'Min €')}
                  {isFreeUser && <Badge variant="secondary" className="ml-1 text-[9px] px-1">Pro</Badge>}
                </Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={minValue}
                  onChange={(e) => {
                    if (isFreeUser) {
                      toast({
                        variant: "destructive",
                        title: t('search.freeLimitTitle', 'Funzionalità limitata'),
                        description: t('search.freeValueRangeBlocked', 'I filtri per valore sono disponibili solo per gli abbonati.'),
                      });
                      return;
                    }
                    setMinValue(e.target.value);
                  }}
                  className={cn("h-9", isFreeUser && "opacity-50 cursor-not-allowed")}
                  disabled={isFreeUser}
                />
              </div>

              <div className="w-[100px] space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t('search.maxValue', 'Max €')}
                  {isFreeUser && <Badge variant="secondary" className="ml-1 text-[9px] px-1">Pro</Badge>}
                </Label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={maxValue}
                  onChange={(e) => {
                    if (isFreeUser) {
                      toast({
                        variant: "destructive",
                        title: t('search.freeLimitTitle', 'Funzionalità limitata'),
                        description: t('search.freeValueRangeBlocked', 'I filtri per valore sono disponibili solo per gli abbonati.'),
                      });
                      return;
                    }
                    setMaxValue(e.target.value);
                  }}
                  className={cn("h-9", isFreeUser && "opacity-50 cursor-not-allowed")}
                  disabled={isFreeUser}
                />
              </div>

              {/* Search Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handleSearch()}
                    disabled={searching}
                    className="h-9"
                  >
                    {searching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <SearchIcon className="w-4 h-4 mr-1" />
                        {t('search.searchButton', 'Cerca')}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.search')}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Row 2: Date Period and Options */}
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-xs text-muted-foreground">{t('search.datePresets.period', 'Periodo')}:</Label>
                {DATE_PRESET_KEYS.filter(key => key !== 'custom').map((key) => (
                  <Button
                    key={key}
                    variant={datePreset === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleDatePreset(key)}
                    className="text-xs h-7 px-3"
                  >
                    {t(`search.datePresets.${key}`, key)}
                  </Button>
                ))}
                
                {/* Custom date inputs */}
                <div className="flex items-center gap-1">
                  <Input
                    type="date"
                    value={dateFrom ? format(dateFrom, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      setDateFrom(e.target.value ? new Date(e.target.value) : undefined);
                      setDatePreset('custom');
                    }}
                    className="h-7 text-xs w-[130px]"
                    placeholder={t('search.from', 'Da')}
                  />
                  <span className="text-xs text-muted-foreground">-</span>
                  <Input
                    type="date"
                    value={dateTo ? format(dateTo, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      setDateTo(e.target.value ? new Date(e.target.value) : undefined);
                      setDatePreset('custom');
                    }}
                    className="h-7 text-xs w-[130px]"
                    placeholder={t('search.to', 'A')}
                  />
                </div>

                {(dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2"
                    onClick={clearDates}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-4 ml-auto">
                <div className="flex items-center gap-2">
                  <Switch
                    id="only-with-deadline"
                    checked={onlyWithDeadline}
                    onCheckedChange={setOnlyWithDeadline}
                  />
                  <Label htmlFor="only-with-deadline" className="text-xs cursor-pointer">
                    {t('search.onlyWithDeadline')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="include-expired"
                    checked={includeExpired}
                    onCheckedChange={setIncludeExpired}
                  />
                  <Label htmlFor="include-expired" className="text-xs cursor-pointer">
                    {t('search.includeExpired')}
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Free user warning banner */}
        {isFreeUser && results.length > 0 && (
          <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertTitle className="text-amber-600 dark:text-amber-400 text-sm">
              {t('search.freeSessionWarningTitle', 'Risultati non salvati')}
            </AlertTitle>
            <AlertDescription className="text-xs text-muted-foreground">
              {t('search.freeSessionWarningDescription', 'I risultati della ricerca non vengono salvati. Se chiudi o ricarichi la pagina, perderai i risultati. Abbonati per salvare lo storico.')}
            </AlertDescription>
          </Alert>
        )}

        {/* Results */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold">
              {t('search.results')} {results.length > 0 && `(${results.length})`}
            </h2>
            
            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateMonitoring}
                    disabled={creatingMonitoring || (!keywords && cpvCodes.length === 0 && !country)}
                  >
                    {creatingMonitoring ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4 mr-1" />
                    )}
                    {t('search.createMonitoringButton')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.createMonitoring')}</p>
                </TooltipContent>
              </Tooltip>
              
              {isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendTestEmail}
                      disabled={sendingTestEmail || results.length === 0}
                    >
                      {sendingTestEmail ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-1" />
                      )}
                      {t('search.sendTestEmailButton')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.sendTestEmail')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {exportEnabled ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportExcel}
                        disabled={results.length === 0}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Excel
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('tooltips.exportExcel')}</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportCSV}
                        disabled={results.length === 0}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        CSV
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('tooltips.exportCSV')}</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/pricing')}
                      className="gap-1 text-muted-foreground"
                    >
                      <Download className="w-4 h-4" />
                      Export
                      <Badge variant="secondary" className="ml-1 text-xs">Pro</Badge>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.exportPro')}</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Send All to Analysis button */}
              {results.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendAllToAnalysis}
                      className="gap-1"
                    >
                      <Brain className="w-4 h-4" />
                      {t('dataAnalysis.sendAllToAnalysis', 'Send all to Analysis')}
                      <Badge variant="secondary" className="ml-1 text-xs">{totalResults}</Badge>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('dataAnalysis.sendAllTooltip', 'Send all search results (all pages) to Data Analysis')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>

          {/* AI Analysis Component */}
          <SearchResultsAI results={results} isSearching={searching} searchQuery={keywords} />

          {results.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center">
                  <FileSearch className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground mb-2">
                    {t('search.noResults', 'Nessun risultato')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('search.noResultsDescription', 'Configura i filtri e avvia una ricerca per trovare bandi di gara')}
                  </p>
                  <p className="text-xs text-muted-foreground/80 mb-4">
                    {t('search.noResultsHint', 'Suggerimento: prova parole chiave più generiche o rimuovi alcuni filtri')}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/monitoring')}
                    className="mt-2"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {t('search.noResultsCta', 'Crea un monitoraggio per ricevere alert automatici')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Visible results - limited for free users */}
                {(isFreeUser ? results.slice(0, FREE_RESULTS_LIMIT) : results).map((result) => (
                  <Card key={result.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline">{result.country}</Badge>
                            {result.notice_type && (
                              <Badge variant="secondary" className="text-xs">
                                {t(`search.noticeTypes.${result.notice_type}`, result.notice_type)}
                              </Badge>
                            )}
                            <span className="text-sm text-muted-foreground">
                              {result.ted_notice_id}
                            </span>
                          </div>
                          <h3 className="font-semibold text-foreground mb-2 line-clamp-2">
                            {result.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {result.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <span className="text-muted-foreground">
                              <strong>{t('search.entity')}:</strong> {result.buyer_name}
                            </span>
                            {result.estimated_value && (
                              <span className="font-medium text-primary">
                                €{result.estimated_value.toLocaleString()}
                              </span>
                            )}
                            {result.deadline_date && (
                              <span className="text-destructive">
                                {t('search.deadline')}: {formatDateLocalized(result.deadline_date)}
                              </span>
                            )}
                          </div>
                          {/* Winner info - display all winners per lot */}
                          {(result.winners && result.winners.length > 0) ? (
                            <div className="mt-2 p-2 bg-muted/50 rounded-md space-y-1">
                              <div className="text-xs font-medium text-muted-foreground mb-1">
                                {result.winners.length === 1 ? `${t('search.winner')}:` : `${t('search.winners')} (${result.winners.length}):`}
                              </div>
                              {result.winners.map((winner, idx) => (
                                <div key={idx} className="flex flex-wrap items-center gap-3 text-sm">
                                  {winner.lotNumber && (
                                    <Badge variant="outline" className="text-xs">
                                      {t('search.lot')} {winner.lotNumber}
                                    </Badge>
                                  )}
                                  <span className="text-foreground font-medium">
                                    {winner.name}
                                  </span>
                                  {winner.vatNumber && (
                                    <span className="text-muted-foreground text-xs">
                                      {t('search.vatNumber')}: {winner.vatNumber}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (result.winner_name || result.winner_vat_number) && (
                            <div className="flex flex-wrap items-center gap-4 text-sm mt-2 p-2 bg-muted/50 rounded-md">
                              {result.winner_name && (
                                <span className="text-foreground">
                                  <strong>{t('search.winner')}:</strong> {result.winner_name}
                                </span>
                              )}
                              {result.winner_vat_number && (
                                <span className="text-muted-foreground">
                                  <strong>{t('search.vatNumber')}:</strong> {result.winner_vat_number}
                                </span>
                              )}
                            </div>
                          )}
                          {result.cpv_codes?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {[...new Set(result.cpv_codes)].slice(0, 3).map((cpv, index) => (
                                <CpvBadge key={`${result.id}-${cpv}-${index}`} code={cpv} showPrefix />
                              ))}
                              {[...new Set(result.cpv_codes)].length > 3 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{[...new Set(result.cpv_codes)].length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {result.pdf_url && (
                          <div className="shrink-0">
                            <Button
                              variant="secondary"
                              size="sm"
                              asChild
                            >
                              <a
                                href={result.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                PDF
                              </a>
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Free user results limit CTA */}
                {isFreeUser && results.length > FREE_RESULTS_LIMIT && (
                  <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                    <CardContent className="py-8 text-center">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">
                        {t('search.freeResultsLimitTitle', 'Altri {{count}} bandi disponibili', { count: results.length - FREE_RESULTS_LIMIT })}
                      </h3>
                      <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                        {t('search.freeResultsLimitDescription', 'Stai visualizzando solo {{shown}} risultati su {{total}}. Abbonati per sbloccare tutti i bandi e le funzionalità avanzate.', { shown: FREE_RESULTS_LIMIT, total: results.length })}
                      </p>
                      <Button onClick={() => navigate('/pricing')} className="gap-2">
                        <TrendingUp className="w-4 h-4" />
                        {t('search.unlockAllResults', 'Sblocca tutti i risultati')}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {/* Pagination - not shown for free users at limit */}
                {totalPages > 1 && !(isFreeUser && results.length >= FREE_RESULTS_LIMIT) && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || searching}
                    >
                      ««
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || searching}
                    >
                      «
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {/* Show page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={searching}
                            className="w-9"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || searching}
                    >
                      »
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || searching}
                    >
                      »»
                    </Button>
                    
                    <span className="text-sm text-muted-foreground ml-2">
                      Pagina {currentPage} di {totalPages} ({totalResults} risultati)
                    </span>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
      </TooltipProvider>

      {/* Send to Analysis Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              {t('dataAnalysis.sendAllToAnalysis', 'Send all to Analysis')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t('dataAnalysis.sendAllDesc', 'All {{count}} results from all pages will be fetched and added to the selected dataset.', { count: totalResults })}
            </p>
            {analysisDatasets.length > 0 && (
              <div>
                <Label className="text-sm">{t('dataAnalysis.existingDataset', 'Existing dataset')}</Label>
                <Select value={selectedAnalysisDatasetId} onValueChange={v => { setSelectedAnalysisDatasetId(v); setNewAnalysisDatasetName(''); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {analysisDatasets.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-sm">{t('dataAnalysis.orNewDataset', 'Or create new dataset')}</Label>
              <Input
                value={newAnalysisDatasetName}
                onChange={e => { setNewAnalysisDatasetName(e.target.value); if (e.target.value) setSelectedAnalysisDatasetId(''); }}
                placeholder={t('dataAnalysis.newDatasetPlaceholder', 'New dataset name...')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalysisDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={confirmSendAllToAnalysis} disabled={sendingAllToAnalysis || (!selectedAnalysisDatasetId && !newAnalysisDatasetName.trim())}>
              {sendingAllToAnalysis ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
              {sendingAllToAnalysis
                ? t('dataAnalysis.fetchingAll', 'Fetching all pages...')
                : t('dataAnalysis.addToDataset', 'Add to Dataset')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}
